/* (a) and (b) — the two numbers the LLM-1 / LLM-2 pipeline is steered by.
 *
 *   (a)  1 − wrong approved / approved   how often an approved label is right
 *   (b)  not approved / all              how much still goes to the crowd
 *
 * READ ONLY. The rows come from tools/load_accuracy.sh (migration 008): a
 * ground-truth version against the decisions of one judge configuration. The
 * HEADLINE leaves out the videos in metric_exclusion; the all-videos figure sits
 * right beside it, never behind a click, so the flattering number cannot be
 * shown alone.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router";
import { api } from "../api/client";
import type { AccuracyReport, AccuracyTotals } from "../api/types";
import { Eyebrow, Spinner } from "../components/ui";
import { aTone, bTone, crowdOf, pct, wrongOf } from "../lib/accuracy";

const TONE: Record<string, string> = {
  good: "text-verdict-approved-ink",
  near: "text-verdict-flagged-ink",
  bad: "text-verdict-rejected-ink",
  none: "text-muted",
};

function Big({ label, value, tone, hint, goal }:
  { label: string; value: string; tone: string; hint: string; goal: string }) {
  return (
    <div className="rounded-panel bg-card shadow-card px-5 py-4 border border-line">
      <Eyebrow>{label}</Eyebrow>
      <div className={`mt-1 text-4xl font-extrabold tracking-tight ${TONE[tone]}`}>{value}</div>
      <div className="mt-1 text-xs text-text2">{hint}</div>
      <div className="text-[11px] text-faint">{goal}</div>
    </div>
  );
}

function Pair({ title, t }: { title: string; t: AccuracyTotals }) {
  return (
    <section className="mt-4">
      <h2 className="text-sm font-semibold text-ink">
        {title} <span className="font-normal text-muted">· {t.videos} videos · {t.subtasks.toLocaleString("en-US")} sub-tasks</span>
      </h2>
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Big label="(a) approved and right" value={pct(t.a_pct)} tone={aTone(t.a_pct)}
          hint={wrongOf(t)} goal="goal ≈ 95%" />
        <Big label="(b) sent to the crowd" value={pct(t.b_pct)} tone={bTone(t.b_pct)}
          hint={crowdOf(t)} goal="goal < 10%" />
      </div>
    </section>
  );
}

export function Accuracy() {
  const [gt, setGt] = useState<string | undefined>();
  const [ev, setEv] = useState<string | undefined>();
  const [rep, setRep] = useState<AccuracyReport | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setErr(null);
    api.accuracy(gt, ev).then((r) => { if (live) setRep(r); })
      .catch((e: Error) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [gt, ev]);

  return (
    <div className="h-full overflow-y-auto">
      <header className="border-b border-line bg-card px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span className="text-sm font-semibold text-teal">Shaip Hub</span>
            <span className="text-xs text-muted">
              <Link to="/" className="hover:text-teal">My jobs</Link> › Accuracy
            </span>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-6">
        <section className="rounded-panel bg-card shadow-card p-5 border border-line">
          <h1 className="text-lg font-extrabold tracking-[-0.01em] text-ink">LLM-1 / LLM-2 accuracy</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted">
            <strong className="font-semibold text-text2">(a)</strong> is the share of sub-tasks the judge
            approved whose LLM-1 label matches the ground truth;{" "}
            <strong className="font-semibold text-text2">(b)</strong> is the share the judge sends to the
            crowd. A row the frame check could not settle counts as half wrong. These are the same sub-tasks,
            decisions and locks the queue and the video screens show for the run.
          </p>
          {rep && rep.gt_versions.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-text2">
              <label className="flex items-center gap-2">
                <Eyebrow>Ground truth</Eyebrow>
                <select className="rounded-chip border border-line bg-card px-2 py-1"
                  value={rep.gt_version ?? ""} onChange={(e) => setGt(e.target.value)}>
                  {rep.gt_versions.map((g) => <option key={g}>{g}</option>)}
                </select>
              </label>
              <label className="flex items-center gap-2">
                <Eyebrow>Judge</Eyebrow>
                <select className="rounded-chip border border-line bg-card px-2 py-1"
                  value={rep.eval_name ?? ""} onChange={(e) => setEv(e.target.value)}>
                  {rep.eval_names.map((g) => <option key={g}>{g}</option>)}
                </select>
              </label>
            </div>
          )}
        </section>

        {err && <p className="mt-4 text-sm text-verdict-rejected-ink">{err}</p>}
        {!rep && !err && <div className="p-8"><Spinner /></div>}
        {rep && !rep.included && !err && (
          <p className="mt-4 text-sm text-muted">
            Nothing loaded yet. Run <code>tools/load_accuracy.sh</code> from the laptop.
          </p>
        )}

        {rep?.included && rep.all && (
          <>
            <Pair title={rep.excluded_count
              ? `Headline — without the ${rep.excluded_count} videos not suitable for AI`
              : "All videos"} t={rep.included} />
            {rep.excluded_count > 0 && <Pair title="All videos" t={rep.all} />}
            <p className="mt-3 text-[11px] text-faint">
              {rep.unscored_decisions} sub-task(s) have no ground-truth event (the crowd merged them into a
              neighbour): they count in (b), and an approved one is left out of (a).
              {rep.subtasks_corrected > 0 &&
                ` ${rep.subtasks_corrected} reviewed sub-task(s) were corrected from the frame check.`}
            </p>

            <section className="mt-6 rounded-panel bg-card shadow-card border border-line overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted">
                  <tr className="border-b border-line">
                    <th className="px-4 py-2 font-medium">Video</th>
                    <th className="px-3 py-2 font-medium text-right">Sub-tasks</th>
                    <th className="px-3 py-2 font-medium text-right">Approved</th>
                    <th className="px-3 py-2 font-medium text-right">Wrong</th>
                    <th className="px-3 py-2 font-medium text-right">(a)</th>
                    <th className="px-3 py-2 font-medium text-right">(b)</th>
                  </tr>
                </thead>
                <tbody>
                  {rep.videos.map((v) => (
                    <tr key={v.video_id} className={`border-b border-line2 ${v.excluded ? "text-faint" : "text-text2"}`}>
                      <td className="px-4 py-1.5">
                        {v.video}
                        {v.excluded && <span className="ml-2 text-[11px]">· excluded ({v.exclusion_reason})</span>}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{v.subtasks}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{v.approved}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{v.wrong}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${v.excluded ? "" : TONE[aTone(v.a_pct)]}`}>{pct(v.a_pct)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{pct(v.b_pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
